import {
  createContext,
  createMemo,
  JSX,
  Match,
  Show,
  Switch,
  useContext,
} from "solid-js";
import c from "class-c";
import { PropsFor } from "solid-u";

import Logo from "@repo/ui/branding/Logo";
import Link from "@repo/ui/navigation/Link";
import UString from "@repo/utils/UString";
import AppRouter from "@repo/utils-solid/AppRouter";

import Fade from "../animation/Fade";

import styles from "./DashboardLayout.module.scss";

declare namespace DashboardLayout {
  type DesktopVariant = "horizontal" | "vertical";
  type Props = {
    tabs: JSX.Element;
    baseUrl?: string;
    children: JSX.Element;
    propsFor?: PropsFor<{ logo?: Logo.Props }>;
    desktopVariant: DesktopVariant;
  };
}

function DashboardLayout({
  baseUrl = "",
  tabs,
  propsFor: _propsFor,
  desktopVariant,
  children,
}: D<DashboardLayout.Props>) {
  const propsFor = PropsFor.createHandler(() => _propsFor);
  return (
    <div
      class={c(styles)`
        dashboard-layout
        desktop-variant:${desktopVariant}
      `}
    >
      <nav>
        <div>
          <Switch>
            <Match when={desktopVariant === "horizontal"}>
              <Logo
                variant="word"
                {...propsFor("logo")}
                class={c`${styles.logo} text:heading-4++ ${propsFor("logo").class}`}
              />
            </Match>
            <Match when={desktopVariant === "vertical"}>
              <Logo
                variant="full"
                {...propsFor("logo")}
                class={c`${styles.logo} text:heading-3++ mb-8 ${propsFor("logo").class}`}
              />
            </Match>
          </Switch>
          <div class={styles.tabs}>
            <Context.Provider
              value={{
                baseUrl: () => baseUrl,
                desktopVariant: () => desktopVariant,
              }}
            >
              {tabs}
            </Context.Provider>
          </div>
        </div>
      </nav>
      <main>{children}</main>
    </div>
  );
}
export default DashboardLayout;

export declare namespace Tab {
  type Props = {
    class?: string;
    path: string;
    long: string;
    icon: string;
    short: string;
  };
}

export function Tab({
  class: className,
  path,
  long,
  icon,
  short,
}: D<Tab.Props>) {
  const context = useContext(Context);
  if (!context)
    throw new Error("Must render Tab inside DashboardLayout tabs prop.");

  const resolvedPath = createMemo(
    () => UString.trim.end(`${context.baseUrl()}${path}`, "/") || "/"
  );
  const active = createMemo(() =>
    path === "/"
      ? AppRouter.pathname === resolvedPath()
      : AppRouter.pathname.startsWith(resolvedPath())
  );

  return (
    <Link
      href={resolvedPath()}
      class={c(styles)`
        tab
        ${{ active }}
      `.c(className)}
      variant="container"
    >
      <div class={`${styles.desktop} text:large`}>
        <Switch>
          <Match when={context.desktopVariant() === "horizontal"}>
            <div>{long}</div>
            <Show when={active()}>
              <Fade transition="var(--normal-s)">
                <div class={styles.indicator} />
              </Fade>
            </Show>
          </Match>
          <Match when={context.desktopVariant() === "vertical"}>{long}</Match>
        </Switch>
      </div>
      <div class={styles.mobile}>
        <div class="icon:medium mb-0.25">{icon}</div>
        <div class="text:mini">{short}</div>
      </div>
    </Link>
  );
}

const Context = createContext<{
  baseUrl: () => string;
  desktopVariant: () => DashboardLayout.DesktopVariant;
}>();
